/**
 * Makes the shopper choose their options before they can buy — and guarantees
 * that what they chose is what the form will add to the cart.
 *
 * A Shopify product page always resolves to some variant, so the picker
 * normally opens with a size already chosen for the shopper. With "Require the
 * shopper to choose" enabled on the variant picker, the page renders with
 * nothing checked and this module replaces Add to cart / Buy now with a
 * "Select size" prompt until every option has been picked.
 *
 * What makes this hard:
 *
 *   1. Choosing one option re-renders the product section server-side, and the
 *      server fills the *other* options with defaults from its own resolution.
 *      Those are guesses, not choices — worse, when the picked combination does
 *      not exist as a variant, resolution lands somewhere else entirely and the
 *      re-render would silently FLIP what the shopper picked. So every pick is
 *      recorded here as option name -> value, and re-asserted onto the DOM
 *      after every mutation.
 *   2. Shopify computes option availability against the resolved variant, which
 *      until the choice is complete can be a colourway the theme hides. Each
 *      fieldset is therefore stamped trusted/untrusted so CSS only shows
 *      unavailable styling once it means something.
 *   3. The form's variant id comes from that same resolution, so on release the
 *      picked combination is verified against the product's real variant matrix
 *      (/products/<handle>.js) and the form id is corrected to the matching
 *      variant. No match -> the buy buttons stay replaced by an "Unavailable"
 *      prompt instead of adding some other variant.
 *   4. An option with exactly one pickable value has nothing to decide, so it
 *      is selected automatically — including when the server left it unchecked
 *      because its own resolution pointed at a hidden colourway.
 *
 * State is published per SCOPE: on <body> for the main product form (the
 * in-flow CTA row and the sticky bar key off it from CSS alone), and on the
 * enclosing <dialog> for quick-add modals, so a modal's gate never leaks into
 * the page or vice versa.
 */

const GATE_ATTRIBUTE = 'data-selection-incomplete';

/** Options the shopper has chosen this page view: product id -> (option name -> value). */
/** @type {Map<string, Map<string, string>>} */
const chosen = new Map();

/** Options auto-selected once because they offered no real choice. */
/** @type {Set<string>} */
const autoPicked = new Set();

/** Product id -> variant matrix from /products/<handle>.js (null = fetch failed). */
/** @type {Map<string, any>} */
const matrices = new Map();
/** @type {Set<string>} */
const matrixRequested = new Set();

/** @param {string} productId @returns {Map<string, string>} */
function chosenFor(productId) {
  let map = chosen.get(productId);
  if (!map) {
    map = new Map();
    chosen.set(productId, map);
  }
  return map;
}

/** @returns {HTMLElement[]} */
function gatedPickers() {
  return [...document.querySelectorAll('variant-picker[data-require-selection="true"]')].map(
    (el) => /** @type {HTMLElement} */ (el)
  );
}

/**
 * The element gate state is published on for a given node: the enclosing
 * dialog (quick-add modal) or the body (main product form).
 * @param {Element} node
 * @returns {HTMLElement}
 */
function scopeOf(node) {
  return /** @type {HTMLElement} */ (node.closest('dialog') ?? document.body);
}

/**
 * Prompt text nodes belonging to a scope. For the body scope, prompts inside
 * dialogs belong to their own scopes and are excluded.
 * @param {HTMLElement} scope
 */
function promptTargetsFor(scope) {
  const all = [...scope.querySelectorAll('[data-selection-prompt-text]')];
  if (scope === document.body) return all.filter((el) => !el.closest('dialog'));
  return all;
}

/**
 * Shape of the rendered picker: option names in display order, how many values
 * each renders, and which are forced (their server-resolved value was hidden).
 * @param {Element} picker
 */
function optionMeta(picker) {
  /** @type {string[]} */
  const order = [];
  /** @type {Map<string, HTMLInputElement[]>} */
  const inputsByName = new Map();
  /** @type {Set<string>} */
  const forced = new Set();

  for (const element of picker.querySelectorAll('input[data-option-name]')) {
    const input = /** @type {HTMLInputElement} */ (element);
    const name = input.dataset.optionName;
    if (!name) continue;

    if (!inputsByName.has(name)) {
      order.push(name);
      inputsByName.set(name, []);
    }
    inputsByName.get(name)?.push(input);

    if (input.closest('[data-force-choice="true"]')) forced.add(name);
  }

  const gated = order.filter((name) => (inputsByName.get(name)?.length ?? 0) > 1 || forced.has(name));

  return { order, inputsByName, gated };
}

/** Kicks off (once) the fetch of the product's real variant matrix. */
function requestMatrix(picker) {
  const productId = /** @type {HTMLElement} */ (picker).dataset.productId ?? '';
  const url = /** @type {HTMLElement} */ (picker).dataset.productUrl;
  if (!productId || !url || matrixRequested.has(productId)) return;

  matrixRequested.add(productId);
  fetch(`${url.split('?')[0]}.js`)
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null)
    .then((data) => {
      matrices.set(productId, data);
      queueSync();
    });
}

/** @param {HTMLElement} scope @param {string} text */
function setPrompt(scope, text) {
  scope.setAttribute(GATE_ATTRIBUTE, '');
  scope.setAttribute('data-selection-prompt', text);
  for (const prompt of promptTargetsFor(scope)) {
    prompt.textContent = text;
  }
}

/** @param {HTMLElement} scope */
function release(scope) {
  scope.removeAttribute(GATE_ATTRIBUTE);
  scope.removeAttribute('data-selection-prompt');
}

/**
 * Verifies the completed selection against the real variant matrix, corrects
 * the form's variant id, and only then lets the buy buttons through.
 * @param {Element} picker
 * @param {Map<string, string>} picked
 * @param {ReturnType<typeof optionMeta>} meta
 */
function releaseOrHold(picker, picked, meta) {
  const scope = scopeOf(picker);
  const productId = /** @type {HTMLElement} */ (picker).dataset.productId ?? '';
  const data = matrices.get(productId);

  if (data === undefined) {
    // Matrix still loading — release optimistically (the server-rendered state)
    // rather than block sales on a fetch; queueSync re-runs on arrival.
    requestMatrix(picker);
    release(scope);
    return;
  }

  if (data === null || !Array.isArray(data.variants)) {
    release(scope);
    return;
  }

  const optionNames = (data.options ?? []).map((option) =>
    typeof option === 'string' ? option : option?.name ?? ''
  );

  const want = optionNames.map((name) => {
    if (picked.has(name)) return picked.get(name);
    // Not shopper-picked (single-value option): trust the checked input.
    const checked = meta.inputsByName.get(name)?.find((input) => input.checked);
    return checked ? checked.value : undefined;
  });

  const match = data.variants.find((variant) =>
    (variant.options ?? []).every(
      (value, index) => want[index] === undefined || value === want[index]
    )
  );

  if (!match) {
    // The picked combination does not exist as a variant. Shopify's resolution
    // would happily point the form at some other variant — never let that
    // reach the cart.
    setPrompt(scope, 'Unavailable');
    return;
  }

  for (const element of scope.querySelectorAll('product-form-component input[name="id"]')) {
    const input = /** @type {HTMLInputElement} */ (element);
    const component = input.closest('product-form-component');
    if (/** @type {HTMLElement} */ (component)?.dataset.productId !== productId) continue;
    // Body-scope corrections must not reach into dialog forms (their own scope).
    if (scope === document.body && component && component.closest('dialog')) continue;
    input.value = String(match.id);
  }

  release(scope);
}

/**
 * Reconciles the DOM with what the shopper actually picked, then publishes the
 * gate state.
 */
function sync() {
  const pickers = gatedPickers();

  /** @type {Map<HTMLElement, HTMLElement>} */
  const pickerByScope = new Map();
  for (const picker of pickers) pickerByScope.set(scopeOf(picker), picker);

  // Scopes with no gated picker are released: the body (single-variant
  // product, feature off, fully resolved ?variant=) and any dialog that
  // carries stale gate state.
  if (!pickerByScope.has(document.body)) {
    release(document.body);
  }
  for (const element of document.querySelectorAll(`dialog[${GATE_ATTRIBUTE}]`)) {
    const dialog = /** @type {HTMLElement} */ (element);
    if (!pickerByScope.has(dialog)) release(dialog);
  }

  if (pickerByScope.size === 0) {
    document.querySelector('.variant-picker--provisional')?.classList.remove('variant-picker--provisional');
    return;
  }

  for (const [, picker] of pickerByScope) {
    syncPicker(picker);
  }
}

/**
 * Runs the gate for one picker, publishing on its own scope.
 * @param {HTMLElement} picker
 */
function syncPicker(picker) {
  const scope = scopeOf(picker);
  const productId = /** @type {HTMLElement} */ (picker).dataset.productId ?? '';
  const picked = chosenFor(productId);
  const meta = optionMeta(picker);
  requestMatrix(picker);

  // 0. A pick is only as real as its input: if a re-render removed the picked
  //    value (value set changed, combined-listing swap), drop the pick so the
  //    option goes back to outstanding instead of silently counting as
  //    answered with nothing to assert.
  for (const [name, value] of [...picked]) {
    const inputs = meta.inputsByName.get(name);
    if (!inputs || !inputs.some((input) => input.value === value)) {
      picked.delete(name);
    }
  }

  // 1. The shopper's picks always win over the server's resolution: re-assert
  //    them, and clear guesses on options they have not touched.
  for (const [name, inputs] of meta.inputsByName) {
    if (!meta.gated.includes(name)) continue;

    const wanted = picked.get(name);
    for (const input of inputs) {
      const shouldBeChecked = wanted !== undefined ? input.value === wanted : false;
      if (input.checked !== shouldBeChecked) input.checked = shouldBeChecked;
      input.dataset.currentChecked = String(shouldBeChecked);
    }
  }

  // 2. An option with exactly one pickable value decides itself. click() (not
  //    just .checked) so the change event fires and the section re-renders
  //    into a coherent state — this also covers the server leaving a lone
  //    value unchecked because its resolution pointed at a hidden colourway.
  for (const name of meta.order) {
    const inputs = meta.inputsByName.get(name) ?? [];
    if (inputs.length !== 1 || picked.has(name)) continue;

    // Keyed by value so a re-render that swaps in a DIFFERENT lone value can
    // be auto-picked once too, while each value is never clicked twice.
    const key = `${productId}::${name}::${inputs[0].value}`;
    if (inputs[0].checked || autoPicked.has(key)) continue;

    autoPicked.add(key);
    inputs[0].click();
    return; // onChange records the pick; the observer re-runs sync.
  }

  // 3. Availability styling is only meaningful for an option once every option
  //    BEFORE it is truly picked (Shopify computes availability against the
  //    selections of preceding options only).
  for (const fieldset of picker.querySelectorAll('fieldset.variant-option')) {
    const name =
      /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input[data-option-name]'))
        ?.dataset.optionName ?? '';
    const index = meta.order.indexOf(name);
    const preceding = meta.order.slice(0, Math.max(index, 0)).filter((n) => meta.gated.includes(n));
    const trusted = preceding.every((n) => picked.has(n));
    fieldset.setAttribute('data-availability-trusted', String(trusted));
  }

  // 4. Chosen/unavailable markers for CSS (plain classes on purpose —
  //    :not(:has()) did not re-evaluate reliably after checking an input).
  const outstanding = meta.gated.filter((name) => {
    if (picked.has(name)) return false;
    // A single-value option that is already checked (server or auto) is done.
    const inputs = meta.inputsByName.get(name) ?? [];
    return !(inputs.length === 1 && inputs[0].checked);
  });

  picker.classList.toggle('variant-picker--provisional', outstanding.length > 0);

  for (const element of picker.querySelectorAll('.variant-option__button-label')) {
    const label = /** @type {HTMLElement} */ (element);
    const input = /** @type {HTMLInputElement | null} */ (
      label.querySelector('input[data-option-value-id]')
    );
    label.classList.toggle('is-chosen', Boolean(input?.checked));
    label.classList.toggle('is-unavailable', input?.dataset.optionAvailable === 'false');
  }

  if (outstanding.length === 0) {
    releaseOrHold(picker, picked, meta);
    return;
  }

  setPrompt(scope, `Select ${outstanding[0].toLowerCase()}`);
}

/** Records an option the shopper picked, then re-evaluates the gate. */
function onChange(/** @type {Event} */ event) {
  const target = /** @type {HTMLElement} */ (event.target);
  const input = /** @type {HTMLInputElement | null} */ (target?.closest?.('[data-option-name]'));
  if (!input) return;

  const picker = target.closest('variant-picker');
  if (!picker || !picker.hasAttribute('data-require-selection')) return;

  const productId = /** @type {HTMLElement} */ (picker).dataset.productId ?? '';
  const name = input.dataset.optionName;
  if (name) chosenFor(productId).set(name, input.value);

  sync();
}

/** Sends the shopper to whatever they still have to pick. */
function onPromptClick(/** @type {Event} */ event) {
  const trigger = /** @type {HTMLElement} */ (event.target)?.closest?.('[data-selection-prompt-button]');
  if (!trigger) return;

  event.preventDefault();

  const triggerScope = scopeOf(trigger);
  const picker = gatedPickers().find((candidate) => scopeOf(candidate) === triggerScope);
  if (!picker) return;

  const productId = /** @type {HTMLElement} */ (picker).dataset.productId ?? '';
  const picked = chosenFor(productId);
  const meta = optionMeta(picker);
  const outstanding = meta.gated.find((name) => !picked.has(name));

  const fieldset = outstanding
    ? picker
        .querySelector(`input[data-option-name="${CSS.escape(outstanding)}"]`)
        ?.closest('fieldset')
    : null;

  const target = fieldset ?? picker;
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });

  // A flash on the group they need is clearer than silently scrolling.
  target.classList.remove('variant-option--needs-attention');
  void /** @type {HTMLElement} */ (target).offsetWidth;
  target.classList.add('variant-option--needs-attention');
}

document.addEventListener('change', onChange, true);
document.addEventListener('click', onPromptClick);

// The product section is re-rendered on every option change, so re-apply after
// the DOM settles. Coalesced with setTimeout rather than rAF — rAF is frozen
// in background tabs.
//
// childList alone is NOT enough: morph updates a structurally identical picker
// by writing the `checked` attribute/property on existing inputs, which
// produces zero childList mutations — the server's phantom re-check would land
// without this observer ever firing. So the `checked` attribute is watched
// too. No feedback loop: sync() writes .checked as a bare property and its
// other writes (data-current-checked, classes, data-availability-trusted, the
// body attributes) are all outside this filter.
let queued = false;
function queueSync() {
  if (queued) return;
  queued = true;
  setTimeout(() => {
    queued = false;
    sync();
  }, 0);
}

const observer = new MutationObserver(queueSync);

function start() {
  sync();
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['checked'],
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}
window.addEventListener('pageshow', queueSync);
